import { HEADER_HEIGHT, NAVBAR_HEIGHT } from "@/types/constants/common";
import { CloseRounded, SearchOutlined } from "@mui/icons-material";
import { Avatar, Chip, IconButton, Input, Stack, Tab, TabList, Tabs, Typography, tabClasses } from "@mui/joy";
import { RefObject, useEffect, useState } from "react";
import { PROFILE_TABS, ProfileTabValue } from "../profileFields";

export const PROFILE_TABS_STICKY_TOP = HEADER_HEIGHT + NAVBAR_HEIGHT;

type ProfileTabsProps = {
  value: ProfileTabValue;
  onChange: (tab: ProfileTabValue) => void;
  searchQuery: string;
  setSearchQuery: (value: string) => void;
  openNotesCount: number;
  name: string;
  profilePicture?: string;
  heroRef: RefObject<HTMLElement>;
};

function ProfileTabs({
  value,
  onChange,
  searchQuery,
  setSearchQuery,
  openNotesCount,
  name,
  profilePicture,
  heroRef,
}: ProfileTabsProps) {
  const [heroHidden, setHeroHidden] = useState(false);

  // once the header scrolls away, keep the agent's name visible in the bar
  useEffect(() => {
    if (!heroRef.current) return;
    const observer = new IntersectionObserver(([entry]) => setHeroHidden(!entry.isIntersecting), {
      rootMargin: `-${PROFILE_TABS_STICKY_TOP}px 0px 0px 0px`,
    });
    observer.observe(heroRef.current);
    return () => observer.disconnect();
  }, [heroRef]);

  const isSearching = !!searchQuery.trim();

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      gap={2}
      sx={{
        position: "sticky",
        top: PROFILE_TABS_STICKY_TOP,
        zIndex: 20,
        px: { xs: 2, md: 4 },
        backgroundColor: "background.surface",
        borderBottom: "1px solid",
        borderColor: "divider",
        boxShadow: heroHidden ? "sm" : "none",
        transition: "box-shadow 150ms",
      }}
    >
      <Stack direction="row" alignItems="center" gap={2} sx={{ minWidth: 0 }}>
        {heroHidden && (
          <Stack
            direction="row"
            alignItems="center"
            gap={1}
            sx={{ flexShrink: 0, display: { xs: "none", lg: "flex" } }}
          >
            <Avatar src={profilePicture || undefined} size="sm" />
            <Typography level="title-sm" noWrap sx={{ maxWidth: 180 }}>
              {name}
            </Typography>
          </Stack>
        )}

        <Tabs
          value={isSearching ? null : value}
          onChange={(_, newValue) => {
            if (newValue) onChange(newValue as ProfileTabValue);
          }}
          sx={{ backgroundColor: "transparent", minWidth: 0 }}
        >
          <TabList
            disableUnderline
            sx={{
              overflowX: "auto",
              scrollbarWidth: "none",
              "&::-webkit-scrollbar": { display: "none" },
              [`& .${tabClasses.root}`]: {
                flex: "none",
                py: 1.75,
                fontSize: "sm",
                fontWeight: "md",
                color: "text.secondary",
                backgroundColor: "transparent",
                "&:hover": { backgroundColor: "transparent", color: "text.primary" },
                [`&.${tabClasses.selected}`]: {
                  color: "primary.600",
                  "&::after": { height: "3px", backgroundColor: "primary.500" },
                },
              },
            }}
          >
            {PROFILE_TABS.map((tab) => (
              <Tab key={tab.value} value={tab.value} indicatorInset>
                {tab.label}
                {tab.value === "notes" && openNotesCount > 0 && (
                  <Chip size="sm" variant="solid" color="warning" sx={{ ml: 0.75, minHeight: 18, fontSize: "10px" }}>
                    {openNotesCount}
                  </Chip>
                )}
              </Tab>
            ))}
          </TabList>
        </Tabs>
      </Stack>

      <Input
        size="sm"
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Escape") setSearchQuery("");
        }}
        placeholder="Find a field (e.g. TICO, SIN)"
        startDecorator={<SearchOutlined />}
        endDecorator={
          searchQuery && (
            <IconButton size="sm" variant="plain" color="neutral" aria-label="Clear" onClick={() => setSearchQuery("")}>
              <CloseRounded />
            </IconButton>
          )
        }
        sx={{ width: { xs: 180, md: 260 }, flexShrink: 0, display: { xs: "none", sm: "flex" } }}
        slotProps={{ input: { "aria-label": "Find a field on this profile" } }}
      />
    </Stack>
  );
}

export default ProfileTabs;
